// /src/pages/AdminSettings.jsx

import React, { useState, useEffect } from 'react';
import { getSystemSettings, updateSystemSettings } from '../../api/adminApi';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    semester_1_month: '',
    semester_1_day: '',
    semester_2_month: '',
    semester_2_day: '',
    academic_year_start_month: '9',
    academic_year_start_day: '22',
    ai_summary_delay_days: '7',
    leader_report_email_enabled: false,
    survey_reminder_email_enabled: true,
    survey_reminder_delay_days: '3',
    ai_summary_min_response_pct: '100',
    survey_link_expiry_days: '30',
  });

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getSystemSettings();
      // Stored setting may be YYYY-MM-DD (legacy) or MM-DD (new). Normalize to month/day.
      const parse = (val) => {
        if (!val) return { m: '', d: '' };
        // YYYY-MM-DD
        const ymatch = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (ymatch) return { m: String(parseInt(ymatch[2],10)), d: String(parseInt(ymatch[3],10)) };
        const mdmatch = val.match(/^(\d{2})-(\d{2})$/);
        if (mdmatch) return { m: String(parseInt(mdmatch[1],10)), d: String(parseInt(mdmatch[2],10)) };
        return { m: '', d: '' };
      };
      const s1 = parse(data.semester_1_deployment_date || '');
      const s2 = parse(data.semester_2_deployment_date || '');
      const acad = parse(data.academic_year_start_date || '09-22');
      setSettings({
        semester_1_month: s1.m,
        semester_1_day: s1.d,
        semester_2_month: s2.m,
        semester_2_day: s2.d,
        academic_year_start_month: acad.m,
        academic_year_start_day: acad.d,
        ai_summary_delay_days: data.ai_summary_delay_days ?? '7',
        leader_report_email_enabled: String(data.leader_report_email_enabled ?? '0').toLowerCase() === '1' || String(data.leader_report_email_enabled ?? '0').toLowerCase() === 'true',
        survey_reminder_email_enabled: String(data.survey_reminder_email_enabled ?? '1').toLowerCase() === '1' || String(data.survey_reminder_email_enabled ?? '1').toLowerCase() === 'true',
        survey_reminder_delay_days: data.survey_reminder_delay_days ?? '3',
        ai_summary_min_response_pct: data.ai_summary_min_response_pct ?? '100',
        survey_link_expiry_days: data.survey_link_expiry_days ?? '7',
      });
    } catch (error) {
      alert(`Error loading settings: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setSettings(prev => ({ ...prev, [name]: checked }));
      return;
    }

    let v = value;

    // Constrain month/day inputs to valid ranges
    if (name.endsWith('_month')) {
      const n = parseInt(value, 10);
      if (isNaN(n) || value === '') {
        v = '';
      } else if (n < 1) {
        v = '1';
      } else if (n > 12) {
        v = '12';
      } else {
        v = String(n);
      }
    }

    if (name.endsWith('_day')) {
      const n = parseInt(value, 10);
      if (isNaN(n) || value === '') {
        v = '';
      } else if (n < 1) {
        v = '1';
      } else if (n > 31) {
        v = '31';
      } else {
        v = String(n);
      }
    }

    if (name === 'ai_summary_delay_days') {
      const n = parseInt(value, 10);
      if (isNaN(n) || value === '') {
        v = '';
      } else if (n < 0) {
        v = '0';
      } else if (n > 365) {
        v = '365';
      } else {
        v = String(n);
      }
    }

    if (name === 'ai_summary_min_response_pct') {
      const n = parseInt(value, 10);
      if (isNaN(n) || value === '') {
        v = '';
      } else if (n < 0) {
        v = '0';
      } else if (n > 100) {
        v = '100';
      } else {
        v = String(n);
      }
    }

    if (name === 'survey_link_expiry_days') {
      const n = parseInt(value, 10);
      if (isNaN(n) || value === '') {
        v = '';
      } else if (n < 0) {
        v = '0';
      } else if (n > 365) {
        v = '365';
      } else {
        v = String(n);
      }
    }

    if (name === 'survey_reminder_delay_days') {
      const n = parseInt(value, 10);
      if (isNaN(n) || value === '') {
        v = '';
      } else if (n < 0) {
        v = '0';
      } else {
        const expiry = parseInt(settings.survey_link_expiry_days, 10);
        const maxAllowed = !isNaN(expiry) && expiry > 0 ? expiry : 365;
        v = String(Math.min(n, maxAllowed));
      }
    }

    setSettings(prev => ({ ...prev, [name]: v }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // API expects MM-DD strings for each semester (month-day). Convert to MM-DD.
      // Validate inputs
      if (!settings.semester_1_month || !settings.semester_1_day || !settings.semester_2_month || !settings.semester_2_day) {
        alert('Please provide both month and day for each semester deployment date.');
        setIsSaving(false);
        return;
      }

      if (!settings.academic_year_start_month || !settings.academic_year_start_day) {
        alert('Please provide both month and day for academic year start date.');
        setIsSaving(false);
        return;
      }

      // Validate numeric ranges
      const s1m = parseInt(settings.semester_1_month, 10);
      const s1d = parseInt(settings.semester_1_day, 10);
      const s2m = parseInt(settings.semester_2_month, 10);
      const s2d = parseInt(settings.semester_2_day, 10);
      const acym = parseInt(settings.academic_year_start_month, 10);
      const acyd = parseInt(settings.academic_year_start_day, 10);

      if (isNaN(s1m) || s1m < 1 || s1m > 12) { alert('Semester 1 month must be between 1 and 12'); setIsSaving(false); return; }
      if (isNaN(s2m) || s2m < 1 || s2m > 12) { alert('Semester 2 month must be between 1 and 12'); setIsSaving(false); return; }
      if (isNaN(s1d) || s1d < 1 || s1d > 31) { alert('Semester 1 day must be between 1 and 31'); setIsSaving(false); return; }
      if (isNaN(s2d) || s2d < 1 || s2d > 31) { alert('Semester 2 day must be between 1 and 31'); setIsSaving(false); return; }
      if (isNaN(acym) || acym < 1 || acym > 12) { alert('Academic year start month must be between 1 and 12'); setIsSaving(false); return; }
      if (isNaN(acyd) || acyd < 1 || acyd > 31) { alert('Academic year start day must be between 1 and 31'); setIsSaving(false); return; }

      const delayDays = parseInt(settings.ai_summary_delay_days, 10);
      const minPct = parseInt(settings.ai_summary_min_response_pct, 10);
      const expiryDays = parseInt(settings.survey_link_expiry_days, 10);
      const reminderDelayDays = parseInt(settings.survey_reminder_delay_days, 10);
      if (isNaN(delayDays) || delayDays < 0 || delayDays > 365) { alert('AI summary delay must be between 0 and 365 days'); setIsSaving(false); return; }
      if (isNaN(minPct) || minPct < 0 || minPct > 100) { alert('AI summary response threshold must be between 0 and 100'); setIsSaving(false); return; }
      if (isNaN(expiryDays) || expiryDays < 0 || expiryDays > 365) { alert('Survey link expiry must be between 0 and 365 days'); setIsSaving(false); return; }
      if (isNaN(reminderDelayDays) || reminderDelayDays < 0) {
        alert('Survey reminder delay must be a non-negative number.');
        setIsSaving(false);
        return;
      }
      if (settings.survey_reminder_email_enabled && reminderDelayDays > (expiryDays > 0 ? expiryDays : 365)) {
        alert('Survey reminder delay must be between 0 and the survey link expiry days.');
        setIsSaving(false);
        return;
      }

      const toMMDD = (m, d) => {
        const mm = String(m).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        return `${mm}-${dd}`;
      };
      const payload = {
        semester_1_deployment_date: toMMDD(s1m, s1d),
        semester_2_deployment_date: toMMDD(s2m, s2d),
        academic_year_start_date: toMMDD(acym, acyd),
        ai_summary_delay_days: String(delayDays),
        leader_report_email_enabled: settings.leader_report_email_enabled ? '1' : '0',
        survey_reminder_email_enabled: settings.survey_reminder_email_enabled ? '1' : '0',
        survey_reminder_delay_days: String(reminderDelayDays),
        ai_summary_min_response_pct: String(minPct),
        survey_link_expiry_days: String(expiryDays),
      };
      await updateSystemSettings(payload);
      alert('System settings saved successfully!');
    } catch (error) {
      alert(`Error saving settings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-indigo-500">Loading system settings...</div>;
  }

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-6 text-indigo-700">System Settings</h2>

      <div className="space-y-8 max-w-3xl">
        <section className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">System Dates</h3>
          <p className="text-sm text-gray-500 mb-4">Core calendar settings used for deployment and academic year logic.</p>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semester 1 Deployment Month/Day</label>
              <div className="flex space-x-2">
                <select name="semester_1_month" value={settings.semester_1_month} onChange={handleChange} disabled={isSaving} className="p-2 border rounded w-1/2">
                  <option value="">Month</option>
                  {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
                <input type="number" min="1" max="31" name="semester_1_day" value={settings.semester_1_day} onChange={handleChange} disabled={isSaving} className="p-2 border rounded w-1/2" placeholder="Day" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semester 2 Deployment Month/Day</label>
              <div className="flex space-x-2">
                <select name="semester_2_month" value={settings.semester_2_month} onChange={handleChange} disabled={isSaving} className="p-2 border rounded w-1/2">
                  <option value="">Month</option>
                  {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
                <input type="number" min="1" max="31" name="semester_2_day" value={settings.semester_2_day} onChange={handleChange} disabled={isSaving} className="p-2 border rounded w-1/2" placeholder="Day" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year Start Date (Month/Day)</label>
              <p className="text-xs text-gray-500 mb-3">The date when a new academic year begins (e.g., Sept 22). Used to calculate which academic year surveys belong to.</p>
              <div className="flex space-x-2">
                <select name="academic_year_start_month" value={settings.academic_year_start_month} onChange={handleChange} disabled={isSaving} className="p-2 border rounded w-1/2">
                  <option value="">Month</option>
                  {[...Array(12)].map((_,i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                </select>
                <input type="number" min="1" max="31" name="academic_year_start_day" value={settings.academic_year_start_day} onChange={handleChange} disabled={isSaving} className="p-2 border rounded w-1/2" placeholder="Day" />
              </div>
            </div>
          </div>
        </section>

        <section className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Survey Delivery</h3>
          <p className="text-sm text-gray-500 mb-4">Controls for survey link validity and reminder/report email behavior.</p>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Survey Link Expiry (days)</label>
              <input
                type="number"
                min="0"
                max="365"
                name="survey_link_expiry_days"
                value={settings.survey_link_expiry_days}
                onChange={handleChange}
                disabled={isSaving}
                className="p-2 border rounded w-full"
                placeholder="30"
              />
              <p className="text-xs text-gray-500 mt-1">Set to 0 for no expiry. Otherwise links expire this many days after being sent.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Survey Reminder Emails Enabled</label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  name="survey_reminder_email_enabled"
                  checked={!!settings.survey_reminder_email_enabled}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="h-4 w-4"
                />
                <span className="text-sm text-gray-700">Send reminder emails to students who have not submitted</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Default is on. Reminders are queued using the task system and must be sent before the survey link expiry.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Survey Reminder Delay (days)</label>
              <input
                type="number"
                min="0"
                max={parseInt(settings.survey_link_expiry_days, 10) > 0 ? parseInt(settings.survey_link_expiry_days, 10) : 365}
                name="survey_reminder_delay_days"
                value={settings.survey_reminder_delay_days}
                onChange={handleChange}
                disabled={isSaving}
                className="p-2 border rounded w-full"
                placeholder="3"
              />
              <p className="text-xs text-gray-500 mt-1">Set how many days after the initial send the reminder is queued. It cannot be later than the survey link expiry.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leader Report Emails Enabled</label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  name="leader_report_email_enabled"
                  checked={!!settings.leader_report_email_enabled}
                  onChange={handleChange}
                  disabled={isSaving}
                  className="h-4 w-4"
                />
                <span className="text-sm text-gray-700">Enable automatic PDF report emails to module leaders</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Default is off. When enabled, leader report emails are queued after the survey link expiry wait period.</p>
            </div>
          </div>
        </section>

        <section className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-1">AI Settings</h3>
          <p className="text-sm text-gray-500 mb-4">Controls for automatic summary timing and response thresholds.</p>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">AI Summary Auto-Generate Delay (days)</label>
              <input
                type="number"
                min="0"
                max="365"
                name="ai_summary_delay_days"
                value={settings.ai_summary_delay_days}
                onChange={handleChange}
                disabled={isSaving}
                className="p-2 border rounded w-full"
                placeholder="30"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-generate after this many days if the response threshold is not met.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">AI Summary Response Threshold (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                name="ai_summary_min_response_pct"
                value={settings.ai_summary_min_response_pct}
                onChange={handleChange}
                disabled={isSaving}
                className="p-2 border rounded w-full"
                placeholder="100"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-generate once this response percentage is reached.</p>
            </div>
          </div>
        </section>
      </div>

      <button
        onClick={handleSave}
        className="mt-8 bg-indigo-600 text-white text-lg font-semibold py-3 px-6 rounded-xl hover:bg-indigo-700 transition shadow-lg disabled:bg-indigo-400"
        disabled={isSaving}
      >
        {isSaving ? 'Saving...' : 'Save System Settings'}
      </button>
    </div>
  );
};

export default AdminSettings;